import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}
function fmtDur(sec) {
  if (!sec) return "—";
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
}
function dispBadge(disp) {
  const map = {
    answered: "bg-teal/15 text-teal border-teal/40",
    missed: "bg-destructive/15 text-destructive border-destructive/40",
    voicemail: "bg-gold/15 text-gold border-gold/40",
    pending: "bg-navy/10 text-navy/70 border-navy/20",
  };
  return `inline-block text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${map[disp] || map.pending}`;
}

/**
 * Priority tag — visually distinct from disposition + outcome.
 *   URGENT   = web-form lead marked ASAP (needs immediate manual callback)
 *   SCHEDULED = any lead flagged request_type=scheduled (booked ahead of time)
 *   LIVE     = call/WhatsApp lead flagged request_type=now (already routed live)
 */
function priorityTag(lead) {
  const ch = lead.channel || "call";
  const rt = lead.request_type || "now";
  if (ch === "web-form" && rt === "now") {
    return { label: "URGENT", cls: "bg-destructive text-cream border-destructive animate-pulse" };
  }
  if (rt === "scheduled") {
    return { label: "SCHEDULED", cls: "bg-gold/20 text-gold border-gold/50" };
  }
  return { label: "LIVE", cls: "bg-teal/15 text-teal border-teal/40" };
}
function outcomeBadge(o) {
  const map = {
    "completed-ride": "bg-teal/15 text-teal border-teal/40",
    "no-show": "bg-destructive/15 text-destructive border-destructive/40",
    "cancelled": "bg-navy/10 text-navy/70 border-navy/30",
    "pending": "bg-cream2 text-navy/60 border-navy/20",
  };
  return `inline-block text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${map[o] || map.pending}`;
}

const OUTCOMES = [
  { value: "pending", label: "Pending" },
  { value: "completed-ride", label: "Completed ride" },
  { value: "no-show", label: "No-show" },
  { value: "cancelled", label: "Cancelled" },
];

export default function LeadLog({ island, reloadKey }) {
  const [leads, setLeads] = useState([]);
  const [disp, setDisp] = useState("all");
  const [outcome, setOutcome] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { island_slug: island };
    if (disp !== "all") params.disposition = disp;
    api.get("/admin/leads", { params })
      .then((r) => setLeads(outcome === "all" ? r.data : r.data.filter((l) => l.outcome === outcome)))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [island, disp, outcome, reloadKey]);

  const updateOutcome = async (leadId, newOutcome) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/leads/${leadId}`, { outcome: newOutcome });
      toast.success("Outcome updated.");
      setSelected(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update outcome.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="lead-log-panel">
      <div className="flex flex-wrap items-end justify-between mb-6 gap-4">
        <div className="flex gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50 mb-1">Disposition</div>
            <Select value={disp} onValueChange={setDisp}>
              <SelectTrigger className="w-[170px] rounded-none border-navy/20 bg-cream" data-testid="lead-disp-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All dispositions</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="voicemail">Voicemail</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50 mb-1">Outcome</div>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger className="w-[170px] rounded-none border-navy/20 bg-cream" data-testid="lead-outcome-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All outcomes</SelectItem>
                {OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="text-sm text-navy/60 font-mono" data-testid="lead-count">{leads.length} leads</div>
      </div>

      <div className="border border-cream2 bg-cream/60 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-cream2 hover:bg-transparent">
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">When</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Priority</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Ch</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Caller</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Pickup</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Operator</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Status</TableHead>
              <TableHead className="text-[10px] uppercase tracking-[0.22em] text-navy/50">Outcome</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center text-navy/50 py-10">Loading…</TableCell></TableRow>
            ) : leads.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-navy/50 py-16 font-display text-xl">No leads match — try Simulate Lead or change filters.</TableCell></TableRow>
            ) : leads.map((l) => {
              const pri = priorityTag(l);
              return (
              <TableRow
                key={l.id}
                onClick={() => setSelected(l)}
                className="border-cream2 cursor-pointer hover:bg-cream2/40"
                data-testid={`lead-row-${l.id}`}
              >
                <TableCell className="text-sm text-navy/80 font-mono whitespace-nowrap">{fmtDate(l.created_at)}</TableCell>
                <TableCell>
                  <span
                    className={`inline-block text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${pri.cls}`}
                    data-testid={`priority-tag-${l.id}`}
                  >
                    {pri.label}
                  </span>
                </TableCell>
                <TableCell className="text-[10px] uppercase tracking-[0.18em] text-navy/60">{(l.channel || "call")}</TableCell>
                <TableCell className="text-sm text-navy/80 font-mono">{l.caller_masked}</TableCell>
                <TableCell className="text-sm text-navy/80 max-w-[220px] truncate">{l.pickup_location || "—"}</TableCell>
                <TableCell className="text-sm text-navy/80">
                  {l.operator_name ? (
                    <span>
                      {l.operator_name}
                      {l.routing_role && (
                        <span className="ml-1 text-[9px] uppercase tracking-[0.18em] text-navy/40">· {l.routing_role}</span>
                      )}
                    </span>
                  ) : <span className="text-navy/40">Unrouted</span>}
                </TableCell>
                <TableCell><span className={dispBadge(l.disposition)}>{l.disposition}</span></TableCell>
                <TableCell><span className={outcomeBadge(l.outcome || "pending")}>{(l.outcome || "pending").replace("-", " ")}</span></TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-none bg-cream border-cream2 max-w-lg" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-navy">Lead detail</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <Row label="Timestamp" value={fmtDate(selected.created_at)} />
              <Row label="Channel" value={(selected.channel || "call").toUpperCase()} />
              <Row label="Request" value={(selected.request_type || "now").toUpperCase()} />
              <Row label="Caller (masked)" value={selected.caller_masked} />
              <Row label="Pickup time" value={selected.pickup_time || "—"} />
              <Row label="Pickup location" value={selected.pickup_location || "—"} />
              <Row label="Drop-off" value={selected.dropoff_location || "—"} />
              <Row label="Operator" value={selected.operator_name || "Unrouted"} />
              <Row label="Routed as" value={selected.routing_role ? selected.routing_role.toUpperCase() : "—"} />
              <Row label="Duration" value={fmtDur(selected.duration_seconds)} />
              <Row label="Disposition" value={<span className={dispBadge(selected.disposition)}>{selected.disposition}</span>} />

              <div className="pt-4 border-t border-cream2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-2">Outcome (manual)</div>
                <Select
                  value={selected.outcome || "pending"}
                  onValueChange={(v) => updateOutcome(selected.id, v)}
                  disabled={saving}
                >
                  <SelectTrigger className="rounded-none border-navy/20 bg-cream" data-testid="lead-outcome-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="mt-2 text-xs text-navy/50">Marking a lead "Completed ride" counts toward the conversion rate on Stats.</p>
              </div>

              <div className="pt-4 border-t border-cream2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50 mb-2">Recording</div>
                {selected.recording_purged_at ? (
                  <div className="text-navy/50 italic text-sm">
                    Recording purged on {new Date(selected.recording_purged_at).toLocaleDateString()} per 90-day retention policy.
                  </div>
                ) : selected.recording_url ? (
                  <audio controls src={selected.recording_url} className="w-full" data-testid="lead-recording-player" />
                ) : (
                  <div className="text-navy/50 italic text-sm">No recording available for this lead.</div>
                )}
              </div>
              {selected.notes && <Row label="Notes" value={selected.notes} />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-6 border-b border-cream2 pb-2">
      <div className="text-[10px] uppercase tracking-[0.22em] text-navy/50">{label}</div>
      <div className="text-sm text-navy text-right">{value}</div>
    </div>
  );
}
